import React, { useState, useEffect, ChangeEvent, MouseEvent } from 'react';
import {
  Box,
  Button,
  TextField,
  MenuItem,
  Card,
  CardContent,
  Typography,
  IconButton,
  Divider,
  Chip,
  Alert,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  FormControlLabel,
  Checkbox,
  Snackbar
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Settings as SettingsIcon,
  ExpandMore as ExpandMoreIcon,
  HourglassEmpty as LoadingIcon
} from '@mui/icons-material';
import { apiClient as api } from '../../api';

interface Rule {
  id?: number;
  rule_key: string;
  rule_name: string;
  description?: string;
  question_key: string;
  operator: 'EQUALS' | 'NOT_EQUALS' | 'CONTAINS' | 'IN' | 'GT' | 'LT';
  expected_value: string;
  action_type: 'EXCLUDE_RISKS' | 'INCLUDE_RISKS' | 'HIGHLIGHT_RISKS' | 'FLAG_FOR_REVIEW';
  target_level: 'L1_CATEGORY' | 'L2_SUBCATEGORY' | 'L3_RISK' | 'L4_DETAILED_RISK' | 'CUSTOM';
  target_risk_ids: number[];
  rationale?: string;
  allow_override: boolean;
  estimated_impact?: number;
  priority: number;
  is_active: boolean;
}

interface Question {
  id?: number;
  question_key: string;
  question_text: string;
  answer_type: string;
  answer_options?: string[];
  rule_name?: string;
}

interface RulesStepProps {
  tenantId?: number;
  questions: Question[];
  onNext: (rules: Rule[]) => void;
  onBack: () => void;
  initialRules?: Rule[];
}

const RulesStep: React.FC<RulesStepProps> = ({
  tenantId,
  questions,
  onNext,
  onBack,
  initialRules = []
}) => {
  const [rules, setRules] = useState<Rule[]>(initialRules);
  const [riskTaxonomy, setRiskTaxonomy] = useState<any[]>([]);
  const [level2Risks, setLevel2Risks] = useState<any[]>([]);
  const [level3Risks, setLevel3Risks] = useState<any[]>([]);
  const [level4Risks, setLevel4Risks] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error' | 'warning' | 'info';
  }>({
    open: false,
    message: '',
    severity: 'info'
  });

  useEffect(() => {
    const initializeData = async () => {
      await loadRiskTaxonomy();
      if (initialRules.length === 0) {
        await loadRules();
      }
    };
    initializeData();
  }, []);

  const showNotification = (message: string, severity: 'success' | 'error' | 'warning' | 'info' = 'info') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const loadRules = async () => {
    setLoading(true);
    try {
      const response = await api.get('/api/risk-scoping/rules', {
        params: { tenant_id: tenantId }
      });
      setRules(response.data);
    } catch (err: any) {
      console.error('Failed to load rules:', err);
      showNotification('Failed to load rules: ' + err.message, 'error');
    } finally {
      setLoading(false);
    }
  };

  const loadRiskTaxonomy = async () => {
    setLoading(true);
    try {
      const [taxonomyResponse, level2Response, level3Response, level4Response] = await Promise.all([
        api.get('/api/risk-scoping/risk-taxonomy'),
        api.get('/api/risk-scoping/level2-risks'),
        api.get('/api/risk-scoping/level3-risks'),
        api.get('/api/risk-scoping/level4-risks')
      ]);

      if (!taxonomyResponse.data || taxonomyResponse.data.length === 0) {
        showNotification('No risk taxonomy found. Please add risks in Admin Dashboard first.', 'warning');
      } else {
        setRiskTaxonomy(taxonomyResponse.data);
        setLevel2Risks(level2Response.data);
        setLevel3Risks(level3Response.data);
        setLevel4Risks(level4Response.data);

        const totalRisks = level2Response.data.length + level3Response.data.length + level4Response.data.length;
        showNotification(`Loaded ${taxonomyResponse.data.length} L1 categories, ${totalRisks} total risks (L2-L4) from taxonomy`, 'success');
      }
    } catch (err: any) {
      console.error('❌ Failed to load risk taxonomy:', err);
      showNotification('Failed to load risk taxonomy: ' + err.message, 'error');
    } finally {
      setLoading(false);
    }
  };

  const addNewRule = () => {
    const newRule: Rule = {
      rule_key: '',
      rule_name: '',
      question_key: questions[0]?.question_key || '',
      operator: 'EQUALS',
      expected_value: '',
      action_type: 'EXCLUDE_RISKS',
      target_level: 'L2_SUBCATEGORY',
      target_risk_ids: [],
      allow_override: false,
      priority: 100,
      is_active: true
    };
    setRules([...rules, newRule]);
  };

  const updateRule = (index: number, field: keyof Rule, value: any) => {
    const updated = [...rules];
    updated[index] = { ...updated[index], [field]: value };
    setRules(updated);
  };

  const deleteRule = async (index: number) => {
    const ruleToDelete = rules[index];

    if (ruleToDelete.id) {
      try {
        await api.delete(`/api/risk-scoping/rules/${ruleToDelete.id}`);
        showNotification('Rule deleted successfully', 'success');
      } catch (err: any) {
        showNotification('Failed to delete rule: ' + err.message, 'error');
        return;
      }
    }

    const updated = rules.filter((_, i) => i !== index);
    setRules(updated);
  };

  const toggleRiskSelection = (ruleIndex: number, riskId: number) => {
    const updated = [...rules];
    const currentIds = updated[ruleIndex].target_risk_ids || [];

    if (currentIds.includes(riskId)) {
      updated[ruleIndex].target_risk_ids = currentIds.filter(id => id !== riskId);
    } else {
      updated[ruleIndex].target_risk_ids = [...currentIds, riskId];
    }

    setRules(updated);
  };

  const selectAllInCategory = (ruleIndex: number, categoryId: number) => {
    const risksInCategory = level2Risks
      .filter(risk => risk.level_1_risk_id === categoryId)
      .map(risk => risk.id);

    const updated = [...rules];
    updated[ruleIndex].target_risk_ids = risksInCategory;
    setRules(updated);
  };

  const saveDraft = async () => {
    setSaving(true);
    try {
      for (const rule of rules) {
        const cleanedRule = {
          ...rule,
          rule_key: rule.rule_key?.trim() || '',
          rule_name: rule.rule_name.trim(),
          description: rule.description?.trim() || '',
          question_key: rule.question_key.trim(),
          expected_value: rule.expected_value.trim(),
          rationale: rule.rationale?.trim() || ''
        };

        if (rule.id) {
          await api.put(`/api/risk-scoping/rules/${rule.id}`, cleanedRule);
        } else {
          const response = await api.post('/api/risk-scoping/rules', {
            ...cleanedRule,
            tenant_id: tenantId
          });
          rule.id = response.data.id;
        }
      }
      showNotification('Draft saved successfully!', 'success');
    } catch (err: any) {
      showNotification('Failed to save draft: ' + err.message, 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleNext = async () => {
    for (const rule of rules) {
      if (!rule.rule_name || !rule.question_key || !rule.expected_value) {
        showNotification('Please fill in all required fields for all rules', 'warning');
        return;
      }
      if (rule.target_risk_ids.length === 0) {
        showNotification(`Rule "${rule.rule_name}" must target at least one risk`, 'warning');
        return;
      }
    }

    await saveDraft();
    onNext(rules);
  };

  const getSelectedQuestion = (questionKey: string) => {
    return questions.find(q => q.question_key === questionKey);
  };

  if (loading) {
    return (
      <Box sx={{ p: 3, display: 'flex', alignItems: 'center', gap: 2 }}>
        <LoadingIcon sx={{ animation: 'spin 1s linear infinite', '@keyframes spin': { '0%': { transform: 'rotate(0deg)' }, '100%': { transform: 'rotate(360deg)' } } }} />
        <Typography>Loading rules and risk taxonomy...</Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Define Filtering Rules
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Create rules that map question answers to risk filtering actions.
          Each rule defines when to exclude, include, or flag specific risks.
        </Typography>

        {questions.length === 0 && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            <Typography variant="body2">
              No questions defined. Please go back to Step 1 to create questions first.
            </Typography>
          </Alert>
        )}

        <Alert severity="info" sx={{ mt: 2, bgcolor: '#e3f2fd' }}>
          <Typography variant="subtitle2" fontWeight="bold" gutterBottom sx={{ color: '#1976d2' }}>
            💡 How It Works
          </Typography>
          <Box component="ol" sx={{ pl: 2, m: 0, '& li': { mb: 1 } }}>
            <li>
              <Typography variant="body2">
                <strong>Click "Add Another Rule"</strong> to create a new filtering rule
              </Typography>
            </li>
            <li>
              <Typography variant="body2">
                <strong>Select a question</strong> from the dropdown in the WHEN section
              </Typography>
            </li>
            <li>
              <Typography variant="body2">
                <strong>Define the condition</strong> (e.g., if answer equals "true")
              </Typography>
            </li>
            <li>
              <Typography variant="body2">
                <strong>Choose action</strong> and select which risks from the taxonomy to exclude/include/flag
              </Typography>
            </li>
          </Box>
          <Divider sx={{ my: 1.5, borderColor: '#bbdefb' }} />
          <Typography variant="caption" color="text.secondary" display="block">
            <strong>Tip:</strong> Each question can have multiple rules with different conditions and exclusions.
          </Typography>
        </Alert>
      </Box>

      {rules.map((rule, rIndex) => (
        <Card key={rIndex} sx={{ mb: 3 }}>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6">
                {rule.rule_name || `Rule ${rIndex + 1}`}
              </Typography>
              <Box>
                <IconButton size="small" color="primary">
                  <SettingsIcon />
                </IconButton>
                <IconButton
                  size="small"
                  color="error"
                  onClick={() => deleteRule(rIndex)}
                >
                  <DeleteIcon />
                </IconButton>
              </Box>
            </Box>

            <TextField
              fullWidth
              label="Rule Name"
              placeholder="e.g., Technology Ownership Exclusion"
              value={rule.rule_name}
              onChange={(e: ChangeEvent<HTMLInputElement>) => updateRule(rIndex, 'rule_name', e.target.value)}
              sx={{ mb: 2 }}
              required
              helperText="Give this rule a descriptive name (e.g., 'Exclude Tech Risks for Finance Dept')"
            />

            <Divider sx={{ my: 2 }}>
              <Chip label="WHEN (Condition)" size="small" color="primary" />
            </Divider>

            <Box sx={{ bgcolor: '#e3f2fd', p: 2, borderRadius: 1, mb: 2 }}>
              {getSelectedQuestion(rule.question_key)?.rule_name && (
                <Alert severity="info" sx={{ mb: 2 }}>
                  <Typography variant="caption" fontWeight="bold">
                    Rule Context: {getSelectedQuestion(rule.question_key)?.rule_name}
                  </Typography>
                </Alert>
              )}
              <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                <TextField
                  select
                  label="Question"
                  value={rule.question_key}
                  onChange={(e: ChangeEvent<HTMLInputElement>) => updateRule(rIndex, 'question_key', e.target.value)}
                  sx={{ flex: 1 }}
                  required
                  helperText={`Key: ${rule.question_key || 'Select a question'}`}
                >
                  {questions.map((q) => (
                    <MenuItem key={q.question_key} value={q.question_key}>
                      <Box>
                        <Typography variant="body2">{q.question_text}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          Key: {q.question_key} | Type: {q.answer_type}
                        </Typography>
                      </Box>
                    </MenuItem>
                  ))}
                </TextField>

                <TextField
                  select
                  label="Operator"
                  value={rule.operator}
                  onChange={(e: ChangeEvent<HTMLInputElement>) => updateRule(rIndex, 'operator', e.target.value)}
                  sx={{ width: 150 }}
                >
                  <MenuItem value="EQUALS">Equals</MenuItem>
                  <MenuItem value="NOT_EQUALS">Not Equals</MenuItem>
                  <MenuItem value="CONTAINS">Contains</MenuItem>
                  <MenuItem value="IN">In</MenuItem>
                </TextField>

                {getSelectedQuestion(rule.question_key)?.answer_type === 'BOOLEAN' ? (
                  <TextField
                    select
                    label="Value"
                    value={rule.expected_value}
                    onChange={(e: ChangeEvent<HTMLInputElement>) => updateRule(rIndex, 'expected_value', e.target.value)}
                    sx={{ flex: 1 }}
                    required
                  >
                    <MenuItem value="true">True (Yes)</MenuItem>
                    <MenuItem value="false">False (No)</MenuItem>
                  </TextField>
                ) : getSelectedQuestion(rule.question_key)?.answer_type === 'SINGLE_SELECT' ? (
                  <TextField
                    select
                    label="Value"
                    value={rule.expected_value}
                    onChange={(e: ChangeEvent<HTMLInputElement>) => updateRule(rIndex, 'expected_value', e.target.value)}
                    sx={{ flex: 1 }}
                    required
                  >
                    {getSelectedQuestion(rule.question_key)?.answer_options?.map((option) => (
                      <MenuItem key={option} value={option}>
                        {option}
                      </MenuItem>
                    ))}
                  </TextField>
                ) : (
                  <TextField
                    label="Value"
                    value={rule.expected_value}
                    onChange={(e: ChangeEvent<HTMLInputElement>) => updateRule(rIndex, 'expected_value', e.target.value)}
                    sx={{ flex: 1 }}
                    required
                    placeholder="Enter value"
                  />
                )}
              </Box>
            </Box>

            <Divider sx={{ my: 2 }}>
              <Chip label="THEN (Action)" size="small" color="success" />
            </Divider>

            <Box sx={{ bgcolor: '#e8f5e9', p: 2, borderRadius: 1, mb: 2 }}>
              <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                <TextField
                  select
                  label="Action Type"
                  value={rule.action_type}
                  onChange={(e: ChangeEvent<HTMLInputElement>) => updateRule(rIndex, 'action_type', e.target.value)}
                  sx={{ flex: 1 }}
                >
                  <MenuItem value="EXCLUDE_RISKS">Exclude Risks</MenuItem>
                  <MenuItem value="INCLUDE_RISKS">Include Risks</MenuItem>
                  <MenuItem value="HIGHLIGHT_RISKS">Highlight Risks</MenuItem>
                  <MenuItem value="FLAG_FOR_REVIEW">Flag for Review</MenuItem>
                </TextField>

                <TextField
                  select
                  label="Target Level"
                  value={rule.target_level}
                  onChange={(e: ChangeEvent<HTMLInputElement>) => updateRule(rIndex, 'target_level', e.target.value)}
                  sx={{ flex: 1 }}
                  helperText="Select which risk level to target"
                >
                  <MenuItem value="L1_CATEGORY">L1 (Category)</MenuItem>
                  <MenuItem value="L2_SUBCATEGORY">L2 (Sub-category)</MenuItem>
                  <MenuItem value="L3_RISK">L3 (Risk)</MenuItem>
                  <MenuItem value="L4_DETAILED_RISK">L4 (Detailed Risk)</MenuItem>
                </TextField>
              </Box>

              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="subtitle2">
                  Target Categories (from Risk Taxonomy):
                </Typography>
                <Chip
                  label={`${rule.target_risk_ids?.length || 0} selected`}
                  size="small"
                  color={rule.target_risk_ids?.length > 0 ? 'success' : 'default'}
                />
              </Box>

              <Box sx={{ maxHeight: 300, overflowY: 'auto', border: '1px solid #ddd', borderRadius: 1, p: 1 }}>
                {riskTaxonomy.length === 0 ? (
                  <Alert severity="warning" sx={{ m: 2 }}>
                    <Typography variant="body2">
                      No risk taxonomy found. Please add risks in the Admin Dashboard first.
                    </Typography>
                  </Alert>
                ) : (
                  riskTaxonomy.map((l1Risk) => (
                    <Accordion key={l1Risk.id}>
                      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Checkbox
                            size="small"
                            onClick={(e: MouseEvent) => e.stopPropagation()}
                            onChange={() => selectAllInCategory(rIndex, l1Risk.id)}
                          />
                          <Typography variant="body2" fontWeight="bold">
                            {l1Risk.name}
                          </Typography>
                        </Box>
                      </AccordionSummary>
                      <AccordionDetails>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                          {/* L2 Risks */}
                          {(rule.target_level === 'L2_SUBCATEGORY' || rule.target_level === 'L1_CATEGORY') && l1Risk.level2Risks?.map((l2Risk: any) => (
                            <Box
                              key={l2Risk.id}
                              sx={{
                                display: 'flex',
                                alignItems: 'flex-start',
                                p: 1.5,
                                border: '1px solid #e0e0e0',
                                borderRadius: 1,
                                bgcolor: rule.target_risk_ids?.includes(l2Risk.id) ? '#e3f2fd' : 'white',
                                '&:hover': { bgcolor: rule.target_risk_ids?.includes(l2Risk.id) ? '#bbdefb' : '#f5f5f5' }
                              }}
                            >
                              <Checkbox
                                size="small"
                                checked={rule.target_risk_ids?.includes(l2Risk.id)}
                                onChange={() => toggleRiskSelection(rIndex, l2Risk.id)}
                                sx={{ mt: -0.5 }}
                              />
                              <Box sx={{ flex: 1, ml: 1 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                                  <Chip
                                    label="L2"
                                    size="small"
                                    sx={{
                                      bgcolor: '#ff9800',
                                      color: 'white',
                                      height: '20px',
                                      fontSize: '0.65rem',
                                      fontWeight: 'bold'
                                    }}
                                  />
                                  <Typography variant="body2" fontWeight="600" color="primary">
                                    {l2Risk.code}
                                  </Typography>
                                  <Typography variant="body2" fontWeight="500">
                                    {l2Risk.name}
                                  </Typography>
                                </Box>
                                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', ml: 4 }}>
                                  {l2Risk.description}
                                </Typography>
                              </Box>
                            </Box>
                          ))}

                          {/* L3 Risks */}
                          {rule.target_level === 'L3_RISK' && l1Risk.level2Risks?.map((l2Risk: any) => (
                            l2Risk.level3Risks?.map((l3Risk: any) => (
                              <Box
                                key={l3Risk.id}
                                sx={{
                                  display: 'flex',
                                  alignItems: 'flex-start',
                                  p: 1.5,
                                  border: '1px solid #e0e0e0',
                                  borderRadius: 1,
                                  bgcolor: rule.target_risk_ids?.includes(l3Risk.id) ? '#e3f2fd' : 'white',
                                  '&:hover': { bgcolor: rule.target_risk_ids?.includes(l3Risk.id) ? '#bbdefb' : '#f5f5f5' }
                                }}
                              >
                                <Checkbox
                                  size="small"
                                  checked={rule.target_risk_ids?.includes(l3Risk.id)}
                                  onChange={() => toggleRiskSelection(rIndex, l3Risk.id)}
                                  sx={{ mt: -0.5 }}
                                />
                                <Box sx={{ flex: 1, ml: 1 }}>
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                                    <Chip
                                      label="L3"
                                      size="small"
                                      sx={{
                                        bgcolor: '#4caf50',
                                        color: 'white',
                                        height: '20px',
                                        fontSize: '0.65rem',
                                        fontWeight: 'bold'
                                      }}
                                    />
                                    <Typography variant="body2" fontWeight="600" color="primary">
                                      {l3Risk.code}
                                    </Typography>
                                    <Typography variant="body2" fontWeight="500">
                                      {l3Risk.name}
                                    </Typography>
                                  </Box>
                                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', ml: 4 }}>
                                    {l3Risk.description}
                                  </Typography>
                                </Box>
                              </Box>
                            ))
                          ))}

                          {/* L4 Risks */}
                          {rule.target_level === 'L4_DETAILED_RISK' && l1Risk.level2Risks?.map((l2Risk: any) => (
                            l2Risk.level3Risks?.map((l3Risk: any) => (
                              l3Risk.level4Risks?.map((l4Risk: any) => (
                                <Box
                                  key={l4Risk.id}
                                  sx={{
                                    display: 'flex',
                                    alignItems: 'flex-start',
                                    p: 1.5,
                                    border: '1px solid #e0e0e0',
                                    borderRadius: 1,
                                    bgcolor: rule.target_risk_ids?.includes(l4Risk.id) ? '#e3f2fd' : 'white',
                                    '&:hover': { bgcolor: rule.target_risk_ids?.includes(l4Risk.id) ? '#bbdefb' : '#f5f5f5' }
                                  }}
                                >
                                  <Checkbox
                                    size="small"
                                    checked={rule.target_risk_ids?.includes(l4Risk.id)}
                                    onChange={() => toggleRiskSelection(rIndex, l4Risk.id)}
                                    sx={{ mt: -0.5 }}
                                  />
                                  <Box sx={{ flex: 1, ml: 1 }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                                      <Chip
                                        label="L4"
                                        size="small"
                                        sx={{
                                          bgcolor: '#2196f3',
                                          color: 'white',
                                          height: '20px',
                                          fontSize: '0.65rem',
                                          fontWeight: 'bold'
                                        }}
                                      />
                                      <Typography variant="body2" fontWeight="600" color="primary">
                                        {l4Risk.code}
                                      </Typography>
                                      <Typography variant="body2" fontWeight="500">
                                        {l4Risk.name}
                                      </Typography>
                                    </Box>
                                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', ml: 4 }}>
                                      {l4Risk.description}
                                    </Typography>
                                  </Box>
                                </Box>
                              ))
                            ))
                          ))}
                        </Box>
                      </AccordionDetails>
                    </Accordion>
                  ))
                )}
              </Box>
            </Box>

            <TextField
              fullWidth
              multiline
              rows={2}
              label="Rationale"
              placeholder="Explain why this rule exists..."
              value={rule.rationale || ''}
              onChange={(e: ChangeEvent<HTMLInputElement>) => updateRule(rIndex, 'rationale', e.target.value)}
              sx={{ mb: 2 }}
            />

            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={rule.allow_override}
                    onChange={(e: ChangeEvent<HTMLInputElement>) => updateRule(rIndex, 'allow_override', e.target.checked)}
                  />
                }
                label="Allow override with approval"
              />

              <TextField
                type="number"
                label="Estimated Impact"
                value={rule.estimated_impact || ''}
                onChange={(e: ChangeEvent<HTMLInputElement>) => updateRule(rIndex, 'estimated_impact', parseInt(e.target.value))}
                sx={{ width: 150 }}
                placeholder="# of risks"
              />
            </Box>
          </CardContent>
        </Card>
      ))}

      <Button
        variant="outlined"
        startIcon={<AddIcon />}
        onClick={addNewRule}
        sx={{ mb: 3 }}
      >
        Add Another Rule
      </Button>

      <Divider sx={{ my: 3 }} />

      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
        <Button onClick={onBack}>
          ← Back
        </Button>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            variant="outlined"
            onClick={saveDraft}
            disabled={saving}
          >
            {saving ? 'Saving...' : 'Save Draft'}
          </Button>
          <Button
            variant="contained"
            onClick={handleNext}
            disabled={rules.length === 0}
          >
            Next: Review & Test →
          </Button>
        </Box>
      </Box>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default RulesStep;
